import { orderLineItemsToCartItems, getOrderDeliveryLines } from '../MyOrders/accountOrdersUtils';
import {
  normalizeCheckoutCartItem,
  normalizeCheckoutPayload,
} from './paymentCheckoutState';
import { formatDeliveryAddressLines } from './PaymentFulfillmentSummary';

/**
 * @typedef {{
 *   orderId: string,
 *   orderNumber: string,
 *   grandTotal: number,
 *   cartItems: import('./paymentCheckoutState').CheckoutCartItem[],
 *   fulfillmentType: 'delivery' | 'pickup',
 *   contactLine?: string,
 *   contact?: { name?: string, phone?: string, email?: string },
 *   addressLines?: string[],
 *   charges: {
 *     cartTotal: number,
 *     discount: number,
 *     packagingFee: number,
 *     delivery: number,
 *     gstTax: number,
 *     gstPercent?: number,
 *     grandTotal: number,
 *   },
 *   savedAt?: number,
 * }} PendingOrderPayment
 */

export const PENDING_ORDER_PAYMENT_STORAGE_KEY = 'digirasa_pending_order_payment';

/**
 * @param {unknown} raw
 * @returns {PendingOrderPayment | null}
 */
export function normalizePendingOrderPayment(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const o = /** @type {Record<string, unknown>} */ (raw);
  const orderNumber = String(o.orderNumber || '').trim();
  const orderId = String(o.orderId || '').trim();
  if (!orderNumber) return null;

  const cartItems = Array.isArray(o.cartItems) ? o.cartItems : null;
  if (!cartItems?.length) return null;

  const chargesRaw = o.charges;
  if (!chargesRaw || typeof chargesRaw !== 'object') return null;
  const c = /** @type {Record<string, unknown>} */ (chargesRaw);
  const grandTotal = Number(c.grandTotal ?? o.grandTotal);
  if (!Number.isFinite(grandTotal)) return null;

  const normalizedItems = cartItems
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      return normalizeCheckoutCartItem(/** @type {Record<string, unknown>} */ (row));
    })
    .filter(Boolean);

  if (!normalizedItems.length) return null;

  const contact =
    o.contact && typeof o.contact === 'object'
      ? /** @type {Record<string, unknown>} */ (o.contact)
      : null;

  const addressLines = Array.isArray(o.addressLines)
    ? o.addressLines.map((line) => String(line || '').trim()).filter(Boolean)
    : undefined;

  return {
    orderId: orderId || orderNumber,
    orderNumber,
    grandTotal,
    cartItems: normalizedItems,
    fulfillmentType: o.fulfillmentType === 'pickup' ? 'pickup' : 'delivery',
    contactLine: typeof o.contactLine === 'string' ? o.contactLine : undefined,
    contact: contact
      ? {
          name: typeof contact.name === 'string' ? contact.name : undefined,
          phone: typeof contact.phone === 'string' ? contact.phone : undefined,
          email: typeof contact.email === 'string' ? contact.email : undefined,
        }
      : undefined,
    addressLines,
    charges: {
      cartTotal: Number(c.cartTotal) || 0,
      discount: Number(c.discount) || 0,
      packagingFee: Number(c.packagingFee) || 0,
      delivery: Number(c.delivery) || 0,
      gstTax: Number(c.gstTax) || 0,
      gstPercent: Number(c.gstPercent) || 5,
      grandTotal,
    },
    savedAt: Number(o.savedAt) || undefined,
  };
}

/**
 * @param {{ id: string, displayId: string, total: number | null, orderType: string, raw: Record<string, unknown> }} orderRow
 * @returns {PendingOrderPayment | null}
 */
export function buildPendingOrderPaymentFromOrderRow(orderRow) {
  if (!orderRow?.raw) return null;
  const raw = orderRow.raw;
  const orderNumber = String(orderRow.displayId || raw.orderNumber || '').trim();
  const orderId = String(orderRow.id || raw.id || orderNumber).trim();
  if (!orderNumber) return null;

  const cartItems = orderLineItemsToCartItems(raw)
    .map((item) => normalizeCheckoutCartItem(/** @type {Record<string, unknown>} */ (item)))
    .filter(Boolean);
  if (!cartItems.length) return null;

  const oc = raw.orderCost && typeof raw.orderCost === 'object' ? raw.orderCost : {};
  const cartTotal = Number(oc.subTotal ?? oc.sub_total) || orderRow.total || 0;
  const discount = Number(oc.totalDiscount ?? oc.total_discount) || 0;
  const packagingFee = Number(oc.totalPackagingCharge ?? oc.total_packaging_charge) || 0;
  const delivery = Number(oc.totalDeliveryCharge ?? oc.total_delivery_charge) || 0;
  const gstTax = Number(oc.totalTax ?? oc.total_tax) || 0;
  const grandTotal =
    Number(oc.totalAmount ?? oc.total_amount) ||
    (Number.isFinite(Number(orderRow.total)) ? Number(orderRow.total) : null);
  if (!Number.isFinite(grandTotal)) return null;

  const orderType = String(orderRow.orderType || raw.orderType || '').toLowerCase();
  const fulfillmentType = /pickup/.test(orderType) ? 'pickup' : 'delivery';

  const ui =
    raw.orderUserInformation && typeof raw.orderUserInformation === 'object'
      ? raw.orderUserInformation
      : raw.userInformation && typeof raw.userInformation === 'object'
        ? raw.userInformation
        : null;

  let contact;
  if (ui) {
    const name = String(ui.name || `${ui.firstName || ''} ${ui.lastName || ''}`.trim()).trim();
    const phone = String(ui.mobileNumber || ui.phone || ui.phone_number || '').trim();
    const email = String(ui.emailID || ui.email || '').trim();
    contact = { name: name || undefined, phone: phone || undefined, email: email || undefined };
  }

  const contactLine = contact
    ? [contact.name, contact.phone, contact.email].filter(Boolean).join(' · ')
    : undefined;

  const addressLines =
    fulfillmentType === 'delivery' ? getOrderDeliveryLines(raw) || undefined : undefined;

  return normalizePendingOrderPayment({
    orderId,
    orderNumber,
    grandTotal,
    cartItems,
    fulfillmentType,
    contactLine,
    contact,
    addressLines,
    charges: {
      cartTotal,
      discount,
      packagingFee,
      delivery,
      gstTax,
      gstPercent: 5,
      grandTotal,
    },
    savedAt: Date.now(),
  });
}

/**
 * @param {{ orderId: string | null, orderNumber: string | null }} created
 * @param {import('./paymentCheckoutState').CheckoutPayload} checkoutPayload
 * @returns {PendingOrderPayment | null}
 */
export function buildPendingOrderPaymentFromCheckout(created, checkoutPayload) {
  if (!created?.orderNumber || !checkoutPayload) return null;
  const fulfillmentType =
    checkoutPayload.form.fulfillmentType === 'pickup' ? 'pickup' : 'delivery';
  const addressLines =
    fulfillmentType === 'delivery'
      ? formatDeliveryAddressLines(checkoutPayload.form)
      : undefined;

  return normalizePendingOrderPayment({
    orderId: created.orderId || created.orderNumber,
    orderNumber: created.orderNumber,
    grandTotal: checkoutPayload.charges.grandTotal,
    cartItems: checkoutPayload.cartItems,
    fulfillmentType,
    contactLine: [checkoutPayload.form.fullName, checkoutPayload.form.phone, checkoutPayload.form.email]
      .filter(Boolean)
      .join(' · '),
    contact: {
      name: checkoutPayload.form.fullName || undefined,
      phone: checkoutPayload.form.phone || undefined,
      email: checkoutPayload.form.email || undefined,
    },
    addressLines,
    charges: checkoutPayload.charges,
    savedAt: Date.now(),
  });
}

/** @param {PendingOrderPayment} payment */
export function persistPendingOrderPayment(payment) {
  try {
    sessionStorage.setItem(PENDING_ORDER_PAYMENT_STORAGE_KEY, JSON.stringify(payment));
  } catch {
    /* ignore */
  }
}

/** @returns {PendingOrderPayment | null} */
export function readStoredPendingOrderPayment() {
  try {
    const raw = sessionStorage.getItem(PENDING_ORDER_PAYMENT_STORAGE_KEY);
    if (!raw) return null;
    return normalizePendingOrderPayment(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearStoredPendingOrderPayment() {
  try {
    sessionStorage.removeItem(PENDING_ORDER_PAYMENT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @param {unknown} locationState
 * @returns {PendingOrderPayment | null}
 */
export function resolvePendingOrderPayment(locationState) {
  if (locationState && typeof locationState === 'object') {
    const state = /** @type {{ checkoutPayload?: unknown; pendingOrderPayment?: unknown }} */ (
      locationState
    );
    if (normalizeCheckoutPayload(state.checkoutPayload)) {
      return null;
    }
    const normalized = normalizePendingOrderPayment(state.pendingOrderPayment);
    if (normalized) return normalized;
  }
  return readStoredPendingOrderPayment();
}

/**
 * Resolve how /payment should open. Fresh checkout always wins over stale sessionStorage.
 * @param {unknown} locationState
 * @returns {{
 *   mode: 'checkout' | 'resume';
 *   checkoutPayload: import('./paymentCheckoutState').CheckoutPayload | null;
 *   pendingOrderPayment: PendingOrderPayment | null;
 * }}
 */
export function resolvePaymentPageSession(locationState) {
  const state =
    locationState && typeof locationState === 'object'
      ? /** @type {{ checkoutPayload?: unknown; pendingOrderPayment?: unknown }} */ (locationState)
      : null;

  const checkoutFromNav = state ? normalizeCheckoutPayload(state.checkoutPayload) : null;
  if (checkoutFromNav) {
    return { mode: 'checkout', checkoutPayload: checkoutFromNav, pendingOrderPayment: null };
  }

  const pendingFromNav = state ? normalizePendingOrderPayment(state.pendingOrderPayment) : null;
  if (pendingFromNav) {
    return { mode: 'resume', checkoutPayload: null, pendingOrderPayment: pendingFromNav };
  }

  const pendingStored = readStoredPendingOrderPayment();
  if (pendingStored) {
    return { mode: 'resume', checkoutPayload: null, pendingOrderPayment: pendingStored };
  }

  return { mode: 'checkout', checkoutPayload: null, pendingOrderPayment: null };
}
